import { isFulfilled } from "@reduxjs/toolkit";
import React, { JSX, useState } from "react";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";
import {
  formatAllowance,
  VACANCY_CANDIDATE_VISIBILITY,
  VACANCY_SELECTION_METHOD,
} from "../../../models/vacancy";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { applyForAJob } from "../../../services/candidateJobsService";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { TJobData } from "../../../store/candidate/types";
import { getCentreDetailsThunk } from "../../../store/superadmin/thunks";
import { convertTo12Hour } from "../../../utils/dataParser";
import { formatRelativeTime } from "../../../utils/timeHelper";
import ViewCentre from "../../SuperAdmin/CentresAndUsers/Centres/ViewCentre";
import CustomButton from "../../UI/Buttons/CustomButton";
import CircleCancel from "../../UI/Icons/CircleCancel";
import Location from "../../UI/Icons/Location";
import RoundedTick from "../../UI/Icons/RoundedTick";
import SearchListings from "../../UI/Icons/SearchListings";
import { formatTimeframes } from "../../Vacancy/timeframeApiHelper";
import DeclineApplyJobModal from "./AcceptedListing/DeclineApplyJobModal";
import ExpiringNotifications from "./Elements/ExpiringNotification";
import VacancyDetails from "./Elements/VacancyDetails";
import AcceptJobModal from "./JobListings/ApplyJobModal";
import ConfirmApplyJobModal from "./JobListings/ConfirmApplyJobModal";
import ConflictConfirmApply from "./JobListings/ConflictConfirmApply";
import JobAcceptedConfirmationModal from "./JobListings/JobAcceptedConfirmation";
import WithdrawApplyJobModal from "./PendingListing/WithdrawApplyJobModal";

export function getCandidateLabel({
  selectionMethod,
  visibilityType,
  visibilityUpgraded,
}: {
  selectionMethod: VACANCY_SELECTION_METHOD;
  visibilityType: VACANCY_CANDIDATE_VISIBILITY;
  visibilityUpgraded: VACANCY_CANDIDATE_VISIBILITY | null;
}) {
  // Manual Selection ("First come first served")
  if (
    selectionMethod === VACANCY_SELECTION_METHOD.FIRST_COME_FIRST_SERVED ||
    (visibilityType === VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE &&
      !Boolean(visibilityUpgraded))
  ) {
    return "Accept";
  } else {
    return "Apply";
  }
}

interface CandidateVacancyCardProps {
  /**
   * Vacancy data object containing details about the job vacancy.
   */
  vacancy: TJobData;
  /**
   * Type of the vacancy, which can be either "job" or "project".
   */
  type: string;
  /**
   * Function to fetch the candidate job list after applying for a job.
   * @returns void
   */
  getCandidateJobList?: () => void;

  isCandidateFilledAllForms?: boolean;

  isMobile?: boolean;
  dayAvailability?: any;
  setShowAcceptedJobs?: (show: boolean) => void;
  documentsExpired?: boolean;
  /**
   * Indicates whether to show the expiring document alert.
   */
  isShowExpiringDocAlert?: boolean;
  /**
   * Indicates whether the candidate has expired documents.
   */
  hasExpiredDocs?: boolean;
  /**
   * Index of the last valid job in the candidate's job list.
   */
  lastValidIndex?: number;

  similarJobIndex?: boolean;
}

/**
 * `CandidateVacancyCard` is a React functional component that displays detailed information
 * about a job vacancy. It includes vacancy details, formatted timeframes, posting date,
 * and action buttons for interacting with the vacancy.
 *
 * @component
 * @param {CandidateVacancyCardProps} props - The props for the component.
 * @param {Vacancy} props.vacancy - The vacancy object containing details about the job.
 * @param {string} props.type - The type of the vacancy (e.g., full-time, part-time).
 *
 * @returns {JSX.Element} A styled card component displaying vacancy details and actions.
 *
 * @remarks
 * - The component uses `VacancyDetails` to render detailed information about the vacancy.
 * - Timeframes are formatted using the `formatTimeframes` function and displayed with conditional styling.
 * - Includes two action buttons: "View Profile" and "Accept".
 *
 * @example
 * ```tsx
 * <CandidateVacancyCard
 *   vacancy={{
 *     title: "Software Engineer",
 *     timeframes: [{ label: "2 days left", alert: true }],
 *     posted: "3 days ago",
 *   }}
 *   type="full-time"
 * />
 * ```
 */
const CandidateVacancyCard: React.FC<CandidateVacancyCardProps> = ({
  vacancy,
  type,
  getCandidateJobList,
  isCandidateFilledAllForms,
  isMobile = false,
  dayAvailability,
  setShowAcceptedJobs,
  documentsExpired,
  isShowExpiringDocAlert,
  hasExpiredDocs,
  lastValidIndex,
  similarJobIndex
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const [showAcceptJobModal, setShowAcceptJobModal] = useState(false);
  const [showConflictConfirmModal, setShowConflictConfirmModal] =
    useState(false);
  const [showConfirmApplyJobModal, setShowConfirmApplyJobModal] =
    useState(false);
  const [showJobAcceptedConfirmation, setShowJobAcceptedConfirmation] =
    useState(false);
  const [showCentreViewModal, setShowCentreViewModal] =
    useState<boolean>(false);
  const [isJobApplied, setIsJobApplied] = useState(false);

  const profileData = useAppSelector(
    (state) => state.onboarding.userpersonaldetails
  );

  const isDocumentsExpired = useAppSelector(
        (state) => state.onboarding.userpersonaldetails?.documents_expire_one_month
    );

  const [showWithdrawApplyJobModal, setShowWithdrawApplyJobModal] =
    useState(false);

  const [showDeclineApplyJobModal, setShowDeclineApplyJobModal] =
    useState(false);

  const [isAddressCopied, setIsAddressCopied] = useState(false);

  /**
   * Handles the application process for a job vacancy.
   * @param vacancy - The vacancy object containing necessary details.
   * @returns void
   */
  const handleApplyForJob = async (vacancy: TJobData) => {
    dispatch(showTransparentPreloaderAction());
    try {
      const candidateUuid = profileData?.uuid;

      if (!candidateUuid) {
        toast.error("Please complete your profile before applying for a job.");
        dispatch(hideTransparentPreloaderAction());
        return;
      }

      const actionType =
        getCandidateLabel({
          selectionMethod: vacancy.selection_method as VACANCY_SELECTION_METHOD,
          visibilityType:
            vacancy.visibility_type as VACANCY_CANDIDATE_VISIBILITY,
          visibilityUpgraded: vacancy.visibility_upgraded,
        }) === "Accept"
          ? "accepted"
          : "applied";

      const requestData = {
        action: actionType,
        uuid: vacancy?.uuid,
        candidate_uuid: candidateUuid ?? "",
        selection_method: vacancy?.selection_method,
        isResponseTimeGotOver: Boolean(vacancy?.visibility_upgraded),
      };
      const applyJobResponse = await applyForAJob(dispatch, requestData);

      const payload = (applyJobResponse?.payload ?? {}) as any;
      const restrictionReached = payload?.data?.apply_restricted === true;
      const timeframeConflict = payload?.data?.is_timeframe_conflict === true;

      if (isFulfilled(applyJobResponse)) {
        // toast.success("You have successfully applied for the job.");
        setShowAcceptJobModal(false);
        setShowAcceptedJobs?.(false); // For calendar module apply job
        if (restrictionReached) {
          dispatch(
            showNotificationAction(
              "You can't apply more than five jobs in a day."
            )
          );
          return;
        } else if (actionType === "applied") {
          setShowConfirmApplyJobModal(true);
          setShowAcceptedJobs?.(false); // For calendar module apply job
        } else {
          // dispatch(showNotificationAction("Job Accepted"));
          setShowJobAcceptedConfirmation(true);
          // setTimeout(() => {
          //   getCandidateJobList && getCandidateJobList();
          // }, NOTIFICATION_TIME_OUT);
        }
        setIsJobApplied(true);
      } else {
        if (restrictionReached) {
          handleApplyRestriction();
        } else if (timeframeConflict) {
          handleTimeframeConflict(actionType);
        }
      }
    } catch (error) {
      console.error("Failed to apply for the job:", error);
    } finally {
      dispatch(hideTransparentPreloaderAction());
    }
  };

  const handleApplyRestriction = () => {
    setShowConflictConfirmModal(false);
    setShowAcceptJobModal(false);
    isMobile && setShowAcceptedJobs?.(false);

    dispatch(
      showNotificationAction("You can't apply more than five jobs in a day.")
    );

    setTimeout(() => {
      isMobile && setShowAcceptedJobs?.(true);
    }, NOTIFICATION_TIME_OUT);
    return;
  };

  const handleTimeframeConflict = (actionType: string) => {
    if (actionType === "accepted") {
      setShowAcceptJobModal(false);
      dispatch(
        showNotificationAction(
          "You cannot accept this job due to a conflicting timeframe."
        )
      );
    } else {
      setShowConflictConfirmModal(true);
    }
    return;
  };

  const handleConfirmJobApply = (vacancy: TJobData) => {
    // if(isMobile && setShowAcceptedJobs) { // For calendar module apply job
    //   setShowAcceptedJobs(false);
    // }
    getCandidateLabel({
      selectionMethod: vacancy.selection_method as VACANCY_SELECTION_METHOD,
      visibilityType: vacancy.visibility_type as VACANCY_CANDIDATE_VISIBILITY,
      visibilityUpgraded: vacancy.visibility_upgraded,
    }) === "Accept" || (vacancy?.candidate_past_jobs_count === 0 && vacancy?.is_elv_centre)
      ? setShowAcceptJobModal(true)
      : handleApplyForJob(vacancy);
  };

  const handleWithdrawJobApplication = (vacancy: TJobData) => {
    setShowWithdrawApplyJobModal(true);
  };

  const handleDeclineJobApplication = (vacancy: TJobData) => {
    setShowDeclineApplyJobModal(true);
  };

  const handleView = async (id: number) => {
    setShowCentreViewModal(true);
    dispatch(showTransparentPreloaderAction());
    await dispatch(getCentreDetailsThunk({ centre_id: id }));
    dispatch(hideTransparentPreloaderAction());
  };

  const getActionButtons = (
    pathname: string,
    vacancy: TJobData,
    handleApplyForJob: (vacancy: TJobData) => void
  ) => {
    const buttonConfigs: Record<
      string,
      {
        label: string;
        className: string;
        icon: JSX.Element;
        onClick: () => void;
      }
    > = {
      [AUTH_ROUTE.JOBS]: {
        label: getCandidateLabel({
          selectionMethod: vacancy.selection_method as VACANCY_SELECTION_METHOD,
          visibilityType:
            vacancy.visibility_type as VACANCY_CANDIDATE_VISIBILITY,
          visibilityUpgraded: vacancy.visibility_upgraded,
        }),
        className: "btn-theme bg-teal text-black",
        icon: (
          <RoundedTick
            color={(isCandidateFilledAllForms && !documentsExpired && !vacancy.is_documents_expired) ? "black" : "#aaaaaa"}
          />
        ),
        onClick: () => handleConfirmJobApply(vacancy),
      },
      [AUTH_ROUTE.PENDING_JOBS]: {
        label: "Withdraw",
        className: "btn-theme bg-black text-white",
        icon: <CircleCancel fill="white" />,
        onClick: () => handleWithdrawJobApplication(vacancy),
      },
      [AUTH_ROUTE.ACCEPTED_JOBS]: {
        label: "Decline",
        className: "btn-theme bg-black text-white",
        icon: <CircleCancel fill="white" />,
        onClick: () => handleDeclineJobApplication(vacancy),
      },
    };

    return buttonConfigs[pathname] || null;
  };

  const definePathName = () => {
    if (type === "accepted") {
      return AUTH_ROUTE.ACCEPTED_JOBS;
    } else if (type === "jobs") {
      return AUTH_ROUTE.JOBS;
    } else {
      return location.pathname;
    }
  };

  const actionButton = getActionButtons(
    definePathName(),
    vacancy,
    handleApplyForJob
  );

  const handleCopyAddress = (address: string) => {
    if (address) {
      navigator.clipboard
        .writeText(address)
        .then(() => {
          setIsAddressCopied(true);
        })
        .catch((error) => {
          console.error("Failed to copy address:", error);
          dispatch(showNotificationAction("Failed to copy address."));
        });
    } else {
      dispatch(showNotificationAction("No address available to copy."));
    }
  };
  


  return (
    <>

      {
          similarJobIndex && (
            <div className="w-100 text-center bg-teal-pale-similar p-15px text-teal-extra-dark fw-bold border-bottom border-off-white pb-10px ">
              <SearchListings width={25} height={25} fillColor="#0A625B" />
              We've found jobs with similar hours or in nearby suburbs
            </div>
          )
        }
      {/* <div className={`bg-white p-15px d-flex flex-column gap-20 ${(!isShowExpiringDocAlert && !hasExpiredDocs) ? `border-bottom` : ``} border-off-white`}> */}
      <div className={`bg-white p-15px d-flex flex-column gap-20 border-bottom border-off-white`}>
        
        {(isShowExpiringDocAlert && hasExpiredDocs) && (
          <ExpiringNotifications isShowExpiringDocAlert={isShowExpiringDocAlert} isJobListing={true}/>
        )}

        
        <div className="d-flex flex-column flex-lg-row gap-20 align-items-start align-items-lg-center justify-content-between">
          {/**
           * Position name that match with candidate qualification
           */}

          {isMobile &&
            dayAvailability?.start_time !== null &&
            dayAvailability?.end_time !== null && (
              <div className="fs-22 fw-700 text-black">
                {convertTo12Hour(dayAvailability?.start_time)} to{" "}
                {convertTo12Hour(dayAvailability?.end_time)}
              </div>
            )}

          {isMobile && type === "jobs" && (
            <div className="flex-grow-1 d-flex flex-column flex-lg-row gap-20 align-items-lg-center">
              <div className="min-w-250px w-205px text-break fs-13 fw-400 text-black">
                <span className="d-block d-lg-none fw-700">
                  Requested Timeframe
                </span>
                {vacancy?.timeframes &&
                  formatTimeframes(vacancy?.timeframes)?.map(
                    (formatedTimeData, index) => (
                      <div
                        key={index}
                        className={`fs-13 ${
                          formatedTimeData.alert
                            ? "text-alert fw-700"
                            : "fw-400"
                        }`}
                      >
                        {formatedTimeData.label}
                      </div>
                    )
                  )}
              </div>
              <div className="min-w-250px w-205px text-break fs-13 fw-400 text-black">
                <span className="d-block d-lg-none fw-700">Posted</span>
                {vacancy?.posted ? formatRelativeTime(vacancy?.posted) : ""}
              </div>
              <div className="min-w-250px w-205px text-break fs-13 fw-400 text-black">
                <span className="d-block d-lg-none fw-700">
                  Daily Allowance
                </span>
                {formatAllowance(Number(vacancy?.allowance) ?? undefined, true)}
              </div>
            </div>
          )}

          {/**
           * * Timeframes of the vacancy, formatted using the `formatTimeframes` function.
           */}
          {!isMobile && (
            <div>
              <div className="fs-13 fw-700 d-lg-none">Requested Timeframe</div>
              <div className="min-w-210px w-210px mobile-w-100 text-break fs-13">
                {vacancy?.timeframes &&
                  formatTimeframes(vacancy?.timeframes, true)?.map(
                    (formatedTimeData, index) => (
                      <div
                        key={index}
                        className={`fs-13`}
                        dangerouslySetInnerHTML={{
                          __html: formatedTimeData.label,
                        }}
                      />
                    )
                  )}
              </div>
            </div>
          )}

          <VacancyDetails
            vacancy={vacancy}
            type={type}
            isCandidateFilledAllForms={isCandidateFilledAllForms}
            documentsExpired={documentsExpired}
            isDocumentsExpired={vacancy.is_documents_expired}
          />

          {/**
           * * Posted date of the vacancy.
           */}
          {!isMobile && (
            <div className="ps-mobile-70px">
              <div className="fs-13 fw-700 d-lg-none">Posted</div>
              <div className="min-w-85px fs-13">
                {vacancy?.posted ? formatRelativeTime(vacancy?.posted) : ""}
              </div>
            </div>
          )}

          {type === "pending" && !isMobile && (
            <div className="ps-mobile-70px">
              <div className="fs-13 fw-700 d-lg-none">Applied</div>
              <div className="min-w-85px fs-13">
                {vacancy?.pivot?.applied_at_human
                  ? formatRelativeTime(vacancy?.pivot?.applied_at_human)
                  : ""}
              </div>
            </div>
          )}

          {type === "accepted" && !isMobile && (
            <>
              <div className="ps-mobile-70px">
                <div className="fs-13 fw-700 d-lg-none">Accepted</div>
                <div className="min-w-85px fs-13">
                  {vacancy?.pivot?.accepted_at_human
                    ? formatRelativeTime(vacancy?.pivot?.accepted_at_human)
                    : vacancy?.pivot?.completed_at_human
                    ? formatRelativeTime(vacancy?.pivot?.completed_at_human)
                    : ""}
                </div>
              </div>
            </>
          )}

          {!isMobile && (
            <>
              <div className="ps-mobile-70px">
                <div className="fs-13 fw-700 d-lg-none">Pay Rate</div>
                <div className="min-w-100px w-100px fs-13">
                  {/* {formatAllowance(Number(vacancy?.allowance) ?? undefined)} */}
                  {vacancy &&
                  vacancy.vacancyPositions?.[0].min_base_rate &&
                  vacancy.vacancyPositions?.[0].max_base_rate ? (
                    <div className="fs-13 text-black fw-400">
                      ${vacancy.vacancyPositions?.[0].min_base_rate}-$
                      {vacancy.vacancyPositions?.[0].max_base_rate}/hr{" "}
                      {vacancy?.allowance !== null ? (
                        <span className="fw-700 text-teal-dark">
                          + extra ${vacancy?.allowance}/day allowance
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
              {/* {type === "accepted" && !isMobile && (
                <>
                  {shiftNotes !== "" ? (
                    <div className="ps-mobile-70px">
                      <div
                        className="cursor-pointer"
                        onClick={handleShiftNotesPopup}
                      >
                        <Warning />
                      </div>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </>
              )} */}
            </>
          )}

          {type === "accepted" &&
          !isMobile &&
          vacancy.shift_notes?.length > 0 &&
          vacancy.shift_notes !== "" ? (
            <div className="ps-mobile-70px d-lg-none">
              <div className="fs-13 fw-700">Shift Notes</div>
              <div className="fs-13 fw-400 text-black">
                {vacancy.shift_notes}
              </div>
            </div>
          ) : (
            ""
          )}

          {/**
           * Action buttons for viewing the profile and accepting the vacancy.
           */}
          {type !== "view_centre" && (
            <div className="d-flex flex-column flex-lg-row justify-content-lg-end gap-20 text-nowrap mobile-w-100 min-w-290px">
              {isMobile && type === "accepted" && (
                <>
                  <CustomButton
                    variant={"primary"}
                    type="button"
                    className="btn-theme bg-off-white text-black"
                    onClick={() => {
                      handleCopyAddress(vacancy?.centre?.address);
                      setTimeout(() => setIsAddressCopied(false), 2000);
                    }}
                  >
                    <Location />
                    Copy Address
                  </CustomButton>
                </>
              )}
              {isAddressCopied && (
                <div className="text-teal-dark">Address copied</div>
              )}

              <CustomButton
                variant={"primary"}
                type="button"
                className={`btn-theme ${type === "jobs" ? "btn-off-white" : "bg-teal text-black min-w-126px mobile-w-100"} 
                `}
                onClick={() => handleView(vacancy?.centre?.id)}
                disabled={(isCandidateFilledAllForms && !documentsExpired && !vacancy.is_documents_expired) ? false : true}
              >
                View Profile
              </CustomButton>

              {actionButton && (
                <CustomButton
                  variant={"primary"}
                  type="button"
                  className={`min-w-126px ${actionButton.className}`}
                  onClick={actionButton.onClick}
                  disabled={(isCandidateFilledAllForms && !documentsExpired && !vacancy.is_documents_expired) ? false : true}
                >
                  {actionButton.icon}
                  {actionButton.label}
                </CustomButton>
              )}
            </div>
          )}
        </div>
        {type === "accepted" &&
        !isMobile &&
        vacancy.shift_notes?.length > 0 &&
        vacancy.shift_notes !== "" ? (
          <div className="d-none d-md-flex gap-10 align-items-center">
            <div className="min-w-290px">&nbsp;</div>
            <div className="fs-13 fw-400 text-black">
              <span className="fw-700">Shift Notes:</span> {vacancy.shift_notes}
            </div>
            <div className="min-w-290px"></div>
          </div>
        ) : (
          ""
        )}

        {/* {( isShowExpiringDocAlert && hasExpiredDocs) && lastValidIndex !== -1 && (
          <ExpiringNotifications isShowExpiringDocAlert={isShowExpiringDocAlert} isJobListing={true}/>
        )} */}
      </div>
      {/* when accepting a job */}
      <AcceptJobModal
        showApplyJobModal={showAcceptJobModal}
        setShowApplyJobModal={setShowAcceptJobModal}
        vacancyData={vacancy}
        handleApplyForJob={handleApplyForJob}
      />
      {/* when applying for a job*/}
      <ConfirmApplyJobModal
        onClickClose={getCandidateJobList}
        showApplyJobModal={showConfirmApplyJobModal}
        setShowApplyJobModal={setShowConfirmApplyJobModal}
        vacancyData={vacancy}
        handleApplyForJob={handleApplyForJob}
      />

      <WithdrawApplyJobModal
        getCandidateJobList={getCandidateJobList}
        showApplyJobModal={showWithdrawApplyJobModal}
        setShowApplyJobModal={setShowWithdrawApplyJobModal}
        vacancyData={vacancy}
        handleApplyForJob={handleApplyForJob}
      />

      <DeclineApplyJobModal
        getCandidateJobList={getCandidateJobList}
        showApplyJobModal={showDeclineApplyJobModal}
        setShowApplyJobModal={setShowDeclineApplyJobModal}
        vacancyData={vacancy}
        handleApplyForJob={handleApplyForJob}
      />

      <ConflictConfirmApply
        getCandidateJobList={getCandidateJobList}
        showApplyJobModal={showConflictConfirmModal}
        setShowApplyJobModal={setShowConflictConfirmModal}
        vacancyData={vacancy}
        handleApplyForJob={handleApplyForJob}
        showConfirmApplyJobModal={showConfirmApplyJobModal}
        setShowConfirmApplyJobModal={setShowConfirmApplyJobModal}
        showConflictConfirmModal={showConflictConfirmModal}
        setShowConflictConfirmModal={setShowConflictConfirmModal}
        isMobile={isMobile}
        setShowAcceptedJobs={setShowAcceptedJobs}
      />

      <ViewCentre
        showViewModal={showCentreViewModal}
        setShowViewModal={setShowCentreViewModal}
        handleView={handleView}
        centreId={vacancy?.centre?.id}
        getCandidateJobList={getCandidateJobList}
      />

      <JobAcceptedConfirmationModal
        onClickClose={getCandidateJobList}
        showApplyJobModal={showJobAcceptedConfirmation}
        setShowApplyJobModal={setShowJobAcceptedConfirmation}
        vacancyData={vacancy}
        handleApplyForJob={handleApplyForJob}
      />
    </>
  );
};

export default CandidateVacancyCard;
